/**
 * Role guard component for role-based rendering
 */

import { forwardRef } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import type { RoleGuardProps } from '../types';

/**
 * Role guard component for rendering content based on user roles
 *
 * @example
 * ```tsx
 * <RoleGuard roles="admin">
 *   <AdminPanel />
 * </RoleGuard>
 *
 * <RoleGuard roles={['admin', 'staff']} requireAll={false} fallback={<div>No Access</div>}>
 *   <StaffTools />
 * </RoleGuard>
 * ```
 */
export const RoleGuard = forwardRef<HTMLDivElement, RoleGuardProps>(
  (props, ref) => {
    const { children, fallback = null, roles, requireAll = false } = props;

    const { hasRole, hasAnyRole, hasAllRoles } = usePermissions();

    const roleArray = Array.isArray(roles) ? roles : [roles];

    const hasAccess = requireAll
      ? hasAllRoles(roleArray)
      : hasAnyRole(roleArray);

    if (!hasAccess) {
      return fallback ? <div ref={ref}>{fallback}</div> : null;
    }

    return <div ref={ref}>{children}</div>;
  }
);

RoleGuard.displayName = 'RoleGuard';

export default RoleGuard;
